import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { confirmar } from '../components/confirmacao';
import apiService from '../services/apiService';
import './Regras.css';

/**
 * Regras e Conquistas — o motor de regras da recorder-api.
 *
 * Uma REGRA é uma spec JSON declarativa que a API compila para SQL a partir de um registro de
 * fontes e colunas: ninguém escreve SQL aqui, e os padrões do produto (registro apagado, vendedor
 * ativo, dia em BRT, régua de classificação da empresa) entram sozinhos. Uma CONQUISTA é o que a
 * regra concede (única, múltipla ou com níveis); a regra também pode mandar um PUSH, com ou sem
 * conquista.
 *
 * O construtor cobre o caso comum (uma fonte, uma janela, filtros E, medidas, condição). O que
 * ele não cobre — fórmulas com contexto de fase/meta, OR/NOT, having, ordem — se edita no modo
 * JSON, que é sempre a fonte da verdade: o construtor só mexe nas chaves que conhece e preserva o
 * resto.
 *
 * Ligar uma regra concede conquista e manda push para vendedor de verdade, então o botão mostra
 * ANTES quantos sujeitos batem agora e pede confirmação. A trava de escrita fora de QA mora na
 * API (RULES_WRITE_ENABLED + RULES_ADMIN_IDS), e a rota do BFF é interna (@alia.do).
 */

const SCOPE_LABEL = { salesman: 'Vendedor', store: 'Loja' };
const KIND_LABEL = { single: 'Única', multiple: 'Múltipla', leveled: 'Com níveis' };
const WINDOW_LABEL = {
    today: 'Hoje', yesterday: 'Ontem', week: 'Esta semana', month: 'Este mês', rolling_days: 'Últimos N dias',
    between: 'Entre datas', all: 'Todo o período', phase: 'Fase do vendedor', program: 'Programa do vendedor',
    goal: 'Meta do vendedor',
};
const REDUCE_LABEL = {
    count: 'contar', count_distinct: 'contar distintos', sum: 'somar', avg: 'média', min: 'mínimo', max: 'máximo',
};
const GROUP_REDUCE_LABEL = {
    count: 'quantos grupos', sum: 'somar', avg: 'média', min: 'mínimo', max: 'máximo',
    max_streak: 'maior sequência de dias seguidos',
};
const FREQ_LABEL = {
    once: 'Uma única vez', per_day: 'Uma por dia', per_week: 'Uma por semana', per_phase: 'Uma por fase',
    per_program: 'Uma por programa', unlimited: 'A cada avaliação', window: 'No máximo N a cada H horas',
};
const TRIGGER_LABEL = {
    every_run: 'A cada avaliação', date: 'Numa data', phase_end: 'No fim da fase (ou N dias antes)',
    program_end: 'No fim do programa (ou N dias antes)', goal_end: 'No fim da meta (ou N dias antes)',
};
const CMP = ['>=', '>', '=', '<=', '<', '!=', 'between'];
const SIMPLE_OPS = ['=', '!=', '>', '>=', '<', '<=', 'in', 'not_in', 'contains', 'starts_with', 'is_null', 'not_null'];

const emptySpec = (source = 'recorders') => ({
    source, window: { kind: 'today' }, measures: { n: { reduce: 'count' } },
    condition: { op: '>=', value: 1 },
});
const errMsg = (e, fallback) => {
    const d = e?.response?.data;
    return (typeof d?.detail === 'string' ? d.detail : null) || d?.error || e?.message || fallback;
};
// 403 = a API recusou a escrita neste ambiente (produção: o caminho é QA + Sincronizador). Não é falha
// técnica, então o toast vira aviso (âmbar) em vez de erro (vermelho); o texto vem do `detail` da API.
const errKind = (e) => (e?.response?.status === 403 ? 'warning' : 'error');
const brDateTime = (iso) => {
    if (!iso) return '—';
    const s = String(iso);
    const dt = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : s + 'Z');
    return isNaN(dt) ? s : dt.toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo',
    });
};
const num = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 2 }));
const clone = (o) => JSON.parse(JSON.stringify(o));

// ----------------------------------------------------------------------------- construtor da spec
function ValueInput({ col, op, value, onChange }) {
    if (op === 'is_null' || op === 'not_null') return null;
    if (op === 'between') {
        const [a, b] = Array.isArray(value) ? value : ['', ''];
        return (<>
            <input className="rg-in rg-in-sm" value={a ?? ''} onChange={(e) => onChange([e.target.value, b])} />
            <span> e </span>
            <input className="rg-in rg-in-sm" value={b ?? ''} onChange={(e) => onChange([a, e.target.value])} />
        </>);
    }
    if (op === 'in' || op === 'not_in') {
        return (<input className="rg-in" placeholder="valores separados por vírgula"
            value={Array.isArray(value) ? value.join(', ') : ''}
            onChange={(e) => onChange(e.target.value.split(',').map((x) => x.trim()).filter(Boolean))} />);
    }
    if (col?.type === 'bool') {
        return (<select className="rg-in rg-in-sm" value={String(value)} onChange={(e) => onChange(e.target.value === 'true')}>
            <option value="true">sim</option><option value="false">não</option></select>);
    }
    if (col?.type === 'enum' && col.values) {
        return (<select className="rg-in" value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
            <option value="">—</option>{col.values.map((v) => <option key={v} value={v}>{v}</option>)}</select>);
    }
    if (col?.type === 'int' || col?.type === 'number') {
        return <input className="rg-in rg-in-sm" type="number" value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))} />;
    }
    return <input className="rg-in" type={col?.type === 'date' ? 'date' : 'text'} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />;
}

function SpecBuilder({ spec, onChange, registry, scope }) {
    const src = registry.sources[spec.source];
    const cols = src ? src.columns : [];
    const colBy = (n) => cols.find((c) => c.name === n);
    const set = (patch) => onChange({ ...spec, ...patch });
    const flatWhere = (() => {
        if (!spec.where) return [];
        if (spec.where.and && spec.where.and.every((n) => n.col)) return spec.where.and;
        if (spec.where.col) return [spec.where];
        return null;                      // OR/NOT: só no JSON
    })();
    const setWhere = (list) => set({ where: list.length ? { and: list } : undefined });
    const windows = registry.windows.filter((w) => scope === 'salesman' || !registry.per_subject_windows.includes(w));
    const measures = Object.entries(spec.measures || {});
    const grouped = (spec.group_by || []).length > 0;
    const cond = spec.condition || { op: '>=', value: 1 };
    const setMeasure = (name, patch, rename) => {
        const m = { ...(spec.measures || {}) };
        const cur = { ...(m[name] || {}), ...patch };
        if (cur.col === '') delete cur.col;
        if (rename && rename !== name) { delete m[name]; m[rename] = cur; } else m[name] = cur;
        const v = spec.value;
        set({ measures: m, ...(rename && rename !== name && v && v.ref === name ? { value: { ref: rename } } : {}) });
    };
    const removeMeasure = (name) => { const m = { ...spec.measures }; delete m[name]; set({ measures: m }); };
    const addMeasure = () => {
        let i = measures.length + 1; while ((spec.measures || {})[`m${i}`]) i += 1;
        set({ measures: { ...(spec.measures || {}), [`m${i}`]: { reduce: 'count' } } });
    };

    return (
        <div className="rg-spec">
            <div className="rg-row"><label>Fonte</label>
                <select className="rg-in" value={spec.source} onChange={(e) => onChange(emptySpec(e.target.value))}>
                    {Object.entries(registry.sources).filter(([, s]) => s.scopes.includes(scope)).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
                </select></div>
            <div className="rg-row"><label>Janela</label>
                <select className="rg-in" value={spec.window?.kind} onChange={(e) => {
                    const k = e.target.value;
                    set({ window: k === 'rolling_days' ? { kind: k, days: 7 } : k === 'between' ? { kind: k, from: '', to: '' } : { kind: k } });
                }}>{windows.map((w) => <option key={w} value={w}>{WINDOW_LABEL[w] || w}</option>)}</select>
                {spec.window?.kind === 'rolling_days' && <input className="rg-in rg-in-sm" type="number" min="1" max="366" value={spec.window.days}
                    onChange={(e) => set({ window: { kind: 'rolling_days', days: Number(e.target.value) } })} />}
                {spec.window?.kind === 'between' && <><input className="rg-in" type="date" value={spec.window.from} onChange={(e) => set({ window: { ...spec.window, from: e.target.value } })} />
                    <span> a </span><input className="rg-in" type="date" value={spec.window.to} onChange={(e) => set({ window: { ...spec.window, to: e.target.value } })} /></>}
            </div>

            <div className="rg-block"><div className="rg-block-t">Filtros (todos precisam valer)</div>
                {flatWhere === null && <div className="rg-note">Este filtro usa OU/NÃO — edite no modo JSON.</div>}
                {flatWhere && flatWhere.map((w, i) => {
                    const c = colBy(w.col);
                    const ops = c ? c.ops.filter((o) => SIMPLE_OPS.includes(o)) : SIMPLE_OPS;
                    const upd = (patch) => setWhere(flatWhere.map((x, j) => (j === i ? { ...x, ...patch } : x)));
                    return (<div className="rg-row" key={i}>
                        <select className="rg-in" value={w.col} onChange={(e) => upd({ col: e.target.value, op: '=', value: '' })}>
                            {cols.map((x) => <option key={x.name} value={x.name}>{x.label}</option>)}</select>
                        <select className="rg-in rg-in-sm" value={w.op} onChange={(e) => upd({ op: e.target.value, value: e.target.value === 'in' || e.target.value === 'not_in' ? [] : '' })}>
                            {ops.map((o) => <option key={o} value={o}>{o}</option>)}</select>
                        <ValueInput col={c} op={w.op} value={w.value} onChange={(v) => upd({ value: v })} />
                        <button className="rg-x" onClick={() => setWhere(flatWhere.filter((_, j) => j !== i))}>✕</button>
                    </div>);
                })}
                {flatWhere && <button className="rg-link" onClick={() => setWhere([...flatWhere, { col: cols[0]?.name, op: '=', value: '' }])}>+ filtro</button>}
            </div>

            <div className="rg-block"><div className="rg-block-t">Agrupar por (opcional, até 2)</div>
                <div className="rg-row">
                    {[0, 1].map((i) => (<select key={i} className="rg-in" value={(spec.group_by || [])[i] || ''} onChange={(e) => {
                        const gb = [...(spec.group_by || [])]; if (e.target.value) gb[i] = e.target.value; else gb.splice(i, 1);
                        const next = { ...spec, group_by: gb.filter(Boolean) };
                        if (!next.group_by.length) { delete next.group_by; delete next.reduce_groups; delete next.having; delete next.order_by; delete next.limit; }
                        else if (!next.reduce_groups) next.reduce_groups = 'count';
                        onChange(next);
                    }}><option value="">—</option>{cols.filter((c) => c.groupable).map((c) => <option key={c.name} value={c.name}>{c.label}</option>)}</select>))}
                </div>
                {grouped && <div className="rg-row"><label>Reduzir os grupos</label>
                    <select className="rg-in" value={spec.reduce_groups || 'count'} onChange={(e) => set({ reduce_groups: e.target.value })}>
                        {registry.group_reducers.map((r) => <option key={r} value={r}>{GROUP_REDUCE_LABEL[r] || r}</option>)}</select>
                    <span className="rg-hint">(having, ordem e limite: modo JSON)</span></div>}
            </div>

            <div className="rg-block"><div className="rg-block-t">Medidas</div>
                {measures.map(([name, m]) => {
                    const okCols = cols.filter((c) => (c.reducers || []).includes(m.reduce) || (m.reduce === 'count' && c.type !== 'date'));
                    return (<div className="rg-row" key={name}>
                        <input className="rg-in rg-in-sm" value={name} onChange={(e) => setMeasure(name, {}, e.target.value.replace(/[^a-z0-9_]/g, ''))} />
                        <select className="rg-in" value={m.reduce} onChange={(e) => setMeasure(name, { reduce: e.target.value, col: '' })}>
                            {registry.reducers.map((r) => <option key={r} value={r}>{REDUCE_LABEL[r] || r}</option>)}</select>
                        <select className="rg-in" value={m.col || ''} onChange={(e) => setMeasure(name, { col: e.target.value })}>
                            <option value="">{m.reduce === 'count' ? '(linhas)' : '— coluna —'}</option>
                            {okCols.map((c) => <option key={c.name} value={c.name}>{c.label}</option>)}</select>
                        {measures.length > 1 && <button className="rg-x" onClick={() => removeMeasure(name)}>✕</button>}
                    </div>);
                })}
                <button className="rg-link" onClick={addMeasure}>+ medida</button>
                {measures.length > 1 && <div className="rg-note">Com mais de uma medida, a fórmula do valor (<code>value</code>) é obrigatória — edite no modo JSON.</div>}
            </div>

            <div className="rg-row"><label>Bateu quando o valor for</label>
                <select className="rg-in rg-in-sm" value={cond.op} onChange={(e) => set({ condition: { op: e.target.value, value: e.target.value === 'between' ? [0, 1] : (Array.isArray(cond.value) ? cond.value[0] : cond.value) } })}>
                    {CMP.map((o) => <option key={o} value={o}>{o === 'between' ? 'entre' : o}</option>)}</select>
                {cond.op === 'between'
                    ? <><input className="rg-in rg-in-sm" type="number" value={cond.value?.[0] ?? ''} onChange={(e) => set({ condition: { op: 'between', value: [Number(e.target.value), cond.value?.[1] ?? 0] } })} />
                        <span> e </span><input className="rg-in rg-in-sm" type="number" value={cond.value?.[1] ?? ''} onChange={(e) => set({ condition: { op: 'between', value: [cond.value?.[0] ?? 0, Number(e.target.value)] } })} /></>
                    : <input className="rg-in rg-in-sm" type="number" step="any" value={cond.value ?? ''} onChange={(e) => set({ condition: { op: cond.op, value: Number(e.target.value) } })} />}
            </div>
        </div>
    );
}

// ----------------------------------------------------------------------------- pré-visualização
function PreviewPanel({ data }) {
    if (!data) return null;
    return (
        <div className="rg-preview">
            <div><b>{data.met}</b> de {data.evaluated} {data.scope === 'store' ? 'lojas' : 'vendedores'} batem agora
                <span className="rg-hint"> · janela {brDateTime(data.window?.from)} → {brDateTime(data.window?.to)} (BRT)</span></div>
            <table className="rg-table"><thead><tr><th>{data.scope === 'store' ? 'Loja' : 'Vendedor'}</th><th>Valor</th><th>Bateu</th><th>Fase</th></tr></thead>
                <tbody>{data.rows.slice(0, 15).map((r) => (
                    <tr key={r.subject_id}><td>{r.fullname || r.subject_id}</td><td>{num(r.value)}</td>
                        <td>{r.met ? '✅' : '—'}</td><td>{r.phase_id ?? '—'}</td></tr>))}</tbody></table>
            <details><summary>SQL gerado e parâmetros (confira os padrões injetados)</summary>
                <pre className="rg-sql">{data.sql}</pre><pre className="rg-sql">{JSON.stringify(data.params, null, 1)}</pre></details>
        </div>
    );
}

// ----------------------------------------------------------------------------- formulário da regra
function RuleForm({ initial, achievements, registry, call, notify, onClose, onSaved }) {
    const editing = !!initial?.id;
    const [f, setF] = useState(() => initial ? {
        id: initial.id, key: initial.key, name: initial.name, description: initial.description || '',
        subject_scope: initial.subject_scope, spec: clone(initial.spec), trigger_spec: clone(initial.trigger_spec),
        schedule: clone(initial.schedule), achievement_ids: (initial.achievements || []).map((a) => a.id), active: initial.active,
    } : {
        key: '', name: '', description: '', subject_scope: 'salesman', spec: emptySpec(), trigger_spec: { kind: 'every_run' },
        schedule: { every_minutes: 60 }, achievement_ids: [], active: false,
    });
    const [json, setJson] = useState(null);          // texto do modo JSON; null = construtor
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [pushes, setPushes] = useState(initial?.push_actions || []);
    const set = (patch) => setF((x) => ({ ...x, ...patch }));
    const mine = achievements.filter((a) => a.subject_scope === f.subject_scope && a.active !== false);

    const specAtual = () => {
        if (json === null) return f.spec;
        try { return JSON.parse(json); } catch (e) { throw new Error('JSON da spec inválido: ' + e.message); }
    };
    const abrirJson = () => setJson(JSON.stringify(f.spec, null, 2));
    const voltarConstrutor = () => { try { set({ spec: JSON.parse(json) }); setJson(null); } catch (e) { notify('JSON inválido: ' + e.message, 'error'); } };

    const body = (extra = {}) => ({
        id: f.id, key: f.key, name: f.name, description: f.description || undefined, subject_scope: f.subject_scope,
        spec: specAtual(), trigger_spec: f.trigger_spec, schedule: f.schedule, achievement_ids: f.achievement_ids,
        active: f.active, ...extra,
    });
    const rodar = async (fn) => { setBusy(true); try { await fn(); } catch (e) { notify(errMsg(e, 'Erro'), errKind(e)); } finally { setBusy(false); } };
    const previa = () => rodar(async () => {
        const r = await call('/rules/preview', { spec: specAtual(), subject_scope: f.subject_scope, trigger_spec: f.trigger_spec, limit: 50 });
        if (!r || !Array.isArray(r.rows)) throw new Error('resposta inesperada do preview');
        setPreview(r);
    });
    const salvar = () => rodar(async () => {
        const r = await call('/rules/save', body());
        if (!r || !r.rule) throw new Error('resposta inesperada ao salvar');
        notify(editing ? 'Regra salva' : 'Regra criada (desligada). Ligue na lista depois de conferir o alcance.', 'success');
        onSaved(r.rule);
    });

    const sel = (arr, v, fn) => (<select className="rg-in" value={v} onChange={(e) => fn(e.target.value)}>{arr.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>);
    const trig = f.trigger_spec || { kind: 'every_run' };
    const sched = f.schedule || { every_minutes: 60 };

    return (
        <div className="rg-modal"><div className="rg-card rg-card-lg">
            <h2>{editing ? `Regra #${f.id}` : 'Nova regra'}</h2>
            <div className="rg-grid2">
                <div className="rg-row"><label>Chave</label><input className="rg-in" value={f.key}
                    onChange={(e) => set({ key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })} placeholder="gravou_5_dias" /></div>
                <div className="rg-row"><label>Nome</label><input className="rg-in" value={f.name} onChange={(e) => set({ name: e.target.value })} /></div>
            </div>
            <div className="rg-row"><label>Descrição</label><input className="rg-in rg-in-wide" value={f.description} onChange={(e) => set({ description: e.target.value })} /></div>
            <div className="rg-row"><label>Sujeito</label>
                {sel(Object.entries(SCOPE_LABEL), f.subject_scope, (v) => set({ subject_scope: v, spec: emptySpec(v === 'store' ? 'recorders' : f.spec.source), achievement_ids: [], trigger_spec: { kind: 'every_run' } }))}
                <span className="rg-hint">{f.subject_scope === 'store' ? 'Conta só atendimento de vendedor ativo; concede conquista de loja.' : 'A regra é avaliada para cada vendedor ativo.'}</span></div>

            <h3>O que medir <button className="rg-link" onClick={json === null ? abrirJson : voltarConstrutor}>{json === null ? 'editar em JSON' : 'voltar ao construtor'}</button></h3>
            {json === null
                ? <SpecBuilder spec={f.spec} onChange={(spec) => set({ spec })} registry={registry} scope={f.subject_scope} />
                : <textarea className="rg-json" rows={16} value={json} onChange={(e) => setJson(e.target.value)} spellCheck={false} />}

            <h3>Quando</h3>
            <div className="rg-row"><label>Conceder</label>
                {sel(Object.entries(TRIGGER_LABEL).filter(([k]) => f.subject_scope === 'salesman' || ['every_run', 'date'].includes(k)), trig.kind,
                    (k) => set({ trigger_spec: k === 'date' ? { kind: k, on: '' } : ['phase_end', 'program_end', 'goal_end'].includes(k) ? { kind: k, days_before: 0 } : { kind: k } }))}
                {trig.kind === 'date' && <input className="rg-in" type="date" value={trig.on} onChange={(e) => set({ trigger_spec: { kind: 'date', on: e.target.value } })} />}
                {['phase_end', 'program_end', 'goal_end'].includes(trig.kind) && <><input className="rg-in rg-in-sm" type="number" min="0" max="365" value={trig.days_before}
                    onChange={(e) => set({ trigger_spec: { kind: trig.kind, days_before: Number(e.target.value) } })} /><span> dias antes (0 = no último dia)</span></>}
            </div>
            <div className="rg-row"><label>Avaliar a cada</label>
                {'every_minutes' in sched
                    ? <><input className="rg-in rg-in-sm" type="number" min="5" max="1440" value={sched.every_minutes} onChange={(e) => set({ schedule: { every_minutes: Number(e.target.value) } })} /><span> minutos</span></>
                    : <span className="rg-hint">horários fixos: {JSON.stringify(sched)} (edite pelo MCP)</span>}
            </div>

            <h3>O que fazer quando bater</h3>
            <div className="rg-block"><div className="rg-block-t">Conceder conquista</div>
                {mine.length === 0 && <div className="rg-note">Nenhuma conquista de {SCOPE_LABEL[f.subject_scope].toLowerCase()} ainda — crie na aba Conquistas.</div>}
                {mine.map((a) => (<label key={a.id} className="rg-check"><input type="checkbox" checked={f.achievement_ids.includes(a.id)}
                    onChange={(e) => set({ achievement_ids: e.target.checked ? [...f.achievement_ids, a.id] : f.achievement_ids.filter((x) => x !== a.id) })} />
                    {a.emoji} {a.title} <span className="rg-hint">({KIND_LABEL[a.kind]})</span></label>))}
            </div>

            <div className="rg-actions">
                <button className="rg-btn" disabled={busy} onClick={previa}>👁 Pré-visualizar agora</button>
                <button className="rg-btn rg-primary" disabled={busy || !f.key || !f.name} onClick={salvar}>Salvar</button>
                <button className="rg-btn" onClick={onClose}>Fechar</button>
            </div>
            <PreviewPanel data={preview} />

            {editing && <PushPanel rule={initial} achievements={achievements} pushes={pushes} setPushes={setPushes} call={call} notify={notify} />}
            {!editing && <div className="rg-note">Salve a regra para ligar um push a ela.</div>}
        </div></div>
    );
}

// ----------------------------------------------------------------------------- push da regra
function PushPanel({ rule, achievements, pushes, setPushes, call, notify }) {
    const [p, setP] = useState({ title: '', body: '', scope: 'company', store_id: '', frequency: { kind: 'per_day' }, achievement_id: '', on_level: '' });
    const [sample, setSample] = useState(null);
    const [busy, setBusy] = useState(false);
    const ligadas = achievements.filter((a) => (rule.achievements || []).some((x) => x.id === a.id));
    const chosen = ligadas.find((a) => String(a.id) === String(p.achievement_id));
    const payload = (extra) => ({
        rule_id: rule.id, title: p.title, body: p.body, scope: p.scope,
        store_id: p.scope === 'store' && p.store_id ? Number(p.store_id) : undefined,
        frequency: p.frequency, achievement_id: p.achievement_id ? Number(p.achievement_id) : undefined,
        on_level: p.on_level || undefined, ...extra,
    });
    const rodar = async (fn) => { setBusy(true); try { await fn(); } catch (e) { notify(errMsg(e, 'Erro'), errKind(e)); } finally { setBusy(false); } };
    const recarregar = async () => {
        const r = await call('/rules/get', { id: rule.id });
        if (r?.rule) setPushes(r.rule.push_actions || []);
    };
    const prever = () => rodar(async () => { const r = await call('/rules/push/save', payload({ dry_run: true })); setSample(r.amostra); });
    const criar = () => rodar(async () => {
        const dry = await call('/rules/push/save', payload({ dry_run: true }));
        const n = dry?.amostra?.bateriam_agora ?? 0;
        if (rule.active && !(await confirmar(`A regra está LIGADA: ${n} sujeito(s) receberiam este push já no próximo ciclo. Confirmar?`, { titulo: 'Regra ligada: push imediato', rotuloConfirmar: 'Adicionar push' }))) return;
        await call('/rules/push/save', payload({}));
        notify('Push ligado à regra', 'success'); setSample(null); await recarregar();
    });
    const desligar = (a) => rodar(async () => {
        if (!(await confirmar('Desligar este push da regra? O comunicado continua existindo, mas nada o dispara.', { titulo: 'Desligar push da regra', perigo: true, rotuloConfirmar: 'Desligar' }))) return;
        await call('/rules/push/unlink', { action_id: a.action_id }); notify('Push desligado', 'success'); await recarregar();
    });

    return (
        <div className="rg-block rg-push"><div className="rg-block-t">📲 Push desta regra</div>
            {pushes.length === 0 && <div className="rg-note">Nenhum push ligado.</div>}
            {pushes.map((a) => (<div className="rg-row" key={a.action_id}>
                <span>#{a.communication_id} <b>{a.title}</b> · {FREQ_LABEL[a.frequency?.kind] || a.frequency?.kind}
                    {a.on_level ? ` · ao atingir ${a.on_level}` : ''} {a.communication_active ? '' : '· (comunicado inativo)'}</span>
                <button className="rg-x" onClick={() => desligar(a)}>desligar</button></div>))}
            <div className="rg-sub">Adicionar push</div>
            <div className="rg-row"><label>Título</label><input className="rg-in rg-in-wide" maxLength={65} value={p.title} onChange={(e) => setP({ ...p, title: e.target.value })} placeholder="🥈 <first_name>, você chegou à <achievement_level>!" /></div>
            <div className="rg-row"><label>Texto</label><textarea className="rg-in rg-in-wide" rows={3} maxLength={500} value={p.body} onChange={(e) => setP({ ...p, body: e.target.value })} /></div>
            <div className="rg-hint">Tags: &lt;first_name&gt; &lt;name&gt; &lt;store_name|sua loja&gt; &lt;rule_value&gt; &lt;achievement_title&gt; &lt;achievement_level&gt;</div>
            <div className="rg-row"><label>Público</label>
                <select className="rg-in" value={p.scope} onChange={(e) => setP({ ...p, scope: e.target.value })}>
                    <option value="company">Toda a empresa</option><option value="store">Uma loja</option></select>
                {p.scope === 'store' && <input className="rg-in rg-in-sm" type="number" placeholder="id da loja" value={p.store_id} onChange={(e) => setP({ ...p, store_id: e.target.value })} />}
                <label className="rg-lbl2">Frequência</label>
                <select className="rg-in" value={p.frequency.kind} onChange={(e) => setP({ ...p, frequency: e.target.value === 'window' ? { kind: 'window', max: 1, hours: 24 } : { kind: e.target.value } })}>
                    {Object.entries(FREQ_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
                {p.frequency.kind === 'window' && <><input className="rg-in rg-in-sm" type="number" value={p.frequency.max} onChange={(e) => setP({ ...p, frequency: { ...p.frequency, max: Number(e.target.value) } })} /><span> a cada </span>
                    <input className="rg-in rg-in-sm" type="number" value={p.frequency.hours} onChange={(e) => setP({ ...p, frequency: { ...p.frequency, hours: Number(e.target.value) } })} /><span> h</span></>}
            </div>
            <div className="rg-row"><label>Só ao atingir</label>
                <select className="rg-in" value={p.achievement_id} onChange={(e) => setP({ ...p, achievement_id: e.target.value, on_level: '' })}>
                    <option value="">— qualquer vez que a regra bater —</option>{ligadas.filter((a) => a.kind === 'leveled').map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}</select>
                {chosen && <select className="rg-in" value={p.on_level} onChange={(e) => setP({ ...p, on_level: e.target.value })}>
                    <option value="">— nível —</option>{(chosen.levels || []).map((l) => <option key={l.key} value={l.key}>{l.key}</option>)}</select>}
            </div>
            <div className="rg-actions">
                <button className="rg-btn" disabled={busy || !p.body} onClick={prever}>👁 Ver texto renderizado</button>
                <button className="rg-btn rg-primary" disabled={busy || !p.body || !p.title} onClick={criar}>Ligar push</button></div>
            {sample && (<div className="rg-preview"><div><b>{sample.bateriam_agora}</b> receberiam agora</div>
                {(sample.exemplos || []).map((x) => (<div key={x.salesman_id} className="rg-ex"><b>{x.title}</b><br />{x.body}<div className="rg-hint">{x.fullname}</div></div>))}</div>)}
        </div>
    );
}

// ----------------------------------------------------------------------------- formulário da conquista
function AchievementForm({ initial, call, notify, onClose, onSaved }) {
    const editing = !!initial?.id;
    const [f, setF] = useState(() => initial ? {
        id: initial.id, key: initial.key, title: initial.title, text: initial.text || '', how_to: initial.how_to || '', emoji: initial.emoji || '',
        color: initial.color || '', kind: initial.kind, subject_scope: initial.subject_scope,
        frequency: initial.frequency || { kind: 'once' }, levels: clone(initial.levels || []), sort_order: initial.sort_order, active: initial.active,
    } : {
        key: '', title: '', text: '', how_to: '', emoji: '🏆', color: '#2E7D32', kind: 'multiple', subject_scope: 'salesman',
        frequency: { kind: 'per_day' }, levels: [{ key: 'bronze', min: 10 }, { key: 'prata', min: 25 }, { key: 'ouro', min: 50 }], sort_order: 0, active: true,
    });
    const [busy, setBusy] = useState(false);
    const set = (patch) => setF((x) => ({ ...x, ...patch }));
    const setLevel = (i, patch) => set({ levels: f.levels.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
    const salvar = async () => {
        setBusy(true);
        try {
            const r = await call('/achievements/save', {
                id: f.id, key: f.key, title: f.title, text: f.text || undefined, how_to: f.how_to, emoji: f.emoji || undefined, color: f.color || undefined,
                kind: f.kind, subject_scope: f.subject_scope, sort_order: Number(f.sort_order) || 0, active: f.active,
                frequency: f.kind === 'multiple' ? f.frequency : undefined,
                levels: f.kind === 'leveled' ? f.levels.map((l) => ({ key: l.key, min: Number(l.min), ...(l.design ? { design: l.design } : {}) })) : undefined,
            });
            if (!r || !r.id) throw new Error('resposta inesperada ao salvar');
            notify('Conquista salva', 'success'); onSaved();
        } catch (e) { notify(errMsg(e, 'Erro ao salvar'), errKind(e)); } finally { setBusy(false); }
    };
    return (
        <div className="rg-modal"><div className="rg-card">
            <h2>{editing ? `Conquista #${f.id}` : 'Nova conquista'}</h2>
            <div className="rg-grid2">
                <div className="rg-row"><label>Chave</label><input className="rg-in" value={f.key} onChange={(e) => set({ key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })} /></div>
                <div className="rg-row"><label>Título</label><input className="rg-in" value={f.title} onChange={(e) => set({ title: e.target.value })} /></div>
            </div>
            <div className="rg-row"><label>Texto</label><input className="rg-in rg-in-wide" value={f.text} onChange={(e) => set({ text: e.target.value })} /></div>
            <div className="rg-row"><label>Como conquistar</label><input className="rg-in rg-in-wide" value={f.how_to} onChange={(e) => set({ how_to: e.target.value })} /></div>
            <div className="rg-row"><label>Emoji</label><input className="rg-in rg-in-sm" value={f.emoji} onChange={(e) => set({ emoji: e.target.value })} />
                <label className="rg-lbl2">Cor</label><input type="color" value={f.color || '#2E7D32'} onChange={(e) => set({ color: e.target.value })} />
                <span className="rg-badge" style={{ background: f.color || '#999' }}>{f.emoji} {f.title || 'prévia'}</span></div>
            <div className="rg-row"><label>Sujeito</label>
                <select className="rg-in" value={f.subject_scope} disabled={editing && initial.grants > 0} onChange={(e) => set({ subject_scope: e.target.value })}>
                    {Object.entries(SCOPE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
                <label className="rg-lbl2">Tipo</label>
                <select className="rg-in" value={f.kind} disabled={editing && initial.grants > 0} onChange={(e) => set({ kind: e.target.value })}>
                    {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
            {f.kind === 'multiple' && <div className="rg-row"><label>Frequência</label>
                <select className="rg-in" value={f.frequency.kind} onChange={(e) => set({ frequency: e.target.value === 'window' ? { kind: 'window', max: 1, hours: 24 } : { kind: e.target.value } })}>
                    {Object.entries(FREQ_LABEL).filter(([k]) => f.subject_scope === 'salesman' || !['per_phase', 'per_program'].includes(k)).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
                {f.frequency.kind === 'window' && <><input className="rg-in rg-in-sm" type="number" value={f.frequency.max} onChange={(e) => set({ frequency: { ...f.frequency, max: Number(e.target.value) } })} /><span> a cada </span>
                    <input className="rg-in rg-in-sm" type="number" value={f.frequency.hours} onChange={(e) => set({ frequency: { ...f.frequency, hours: Number(e.target.value) } })} /><span> h</span></>}
            </div>}
            {f.kind === 'leveled' && <div className="rg-block"><div className="rg-block-t">Níveis (faixas numéricas, em ordem crescente)</div>
                <div className="rg-note">O nível é só uma chave e um piso. O visual de cada um (emoji, cor) é do config do app, que casa por <code>chave</code>. O nível nunca regride, e a chave não pode ser removida depois da primeira concessão.</div>
                {f.levels.map((l, i) => (<div className="rg-row" key={i}>
                    <input className="rg-in rg-in-sm" placeholder="chave" value={l.key} onChange={(e) => setLevel(i, { key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })} />
                    <span>a partir de</span><input className="rg-in rg-in-sm" type="number" step="any" value={l.min} onChange={(e) => setLevel(i, { min: e.target.value })} />
                    <button className="rg-x" onClick={() => set({ levels: f.levels.filter((_, j) => j !== i) })}>✕</button></div>))}
                <button className="rg-link" onClick={() => set({ levels: [...f.levels, { key: '', min: (Number(f.levels[f.levels.length - 1]?.min) || 0) + 10 }] })}>+ nível</button></div>}
            <div className="rg-row"><label className="rg-check"><input type="checkbox" checked={f.active} onChange={(e) => set({ active: e.target.checked })} /> Ativa (aparece no app)</label></div>
            <div className="rg-actions">
                <button className="rg-btn rg-primary" disabled={busy || !f.key || !f.title} onClick={salvar}>Salvar</button>
                <button className="rg-btn" onClick={onClose}>Fechar</button></div>
        </div></div>
    );
}

// ----------------------------------------------------------------------------- página
function Regras({ user }) {
    const [toast, setToast] = useState(null);
    const toastTimer = useRef(null);
    // Erro e aviso ficam 8 s (a frase de bloqueio tem duas linhas); o timer anterior é cancelado para
    // um toast novo não ser apagado pelo relógio do antigo.
    const notify = useCallback((msg, type = 'info') => {
        setToast({ msg, type });
        clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToast(null), type === 'error' || type === 'warning' ? 8000 : 5000);
    }, []);
    const [companies, setCompanies] = useState([]);
    const [subdomain, setSubdomain] = useState('');
    const [tab, setTab] = useState('rules');
    const [registry, setRegistry] = useState(null);
    const [rules, setRules] = useState([]);
    const [achs, setAchs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formRule, setFormRule] = useState(null);       // null = fechado; {} = nova
    const [formAch, setFormAch] = useState(null);
    const [grants, setGrants] = useState(null);

    const call = useCallback((path, body = {}) => apiService.rulesCall(path, { subdomain, ...body }), [subdomain]);

    useEffect(() => {
        (async () => {
            try {
                const [c, reg] = await Promise.all([apiService.promptEditorGetCompanies(), apiService.rulesCall('/rules/registry', {})]);
                setCompanies(Array.isArray(c) ? c : []);
                if (!reg || !reg.sources || !Array.isArray(reg.windows)) throw new Error('resposta inesperada do registro (rota do BFF registrada?)');
                setRegistry(reg);
            } catch (e) { notify(errMsg(e, 'Erro ao carregar'), errKind(e)); }
        })();
    }, [notify]);

    const carregar = useCallback(async () => {
        if (!subdomain) { setRules([]); setAchs([]); return; }
        setLoading(true);
        try {
            const [r, a] = await Promise.all([call('/rules/list'), call('/achievements/list')]);
            if (!r || !Array.isArray(r.rules)) throw new Error('resposta inesperada da lista de regras');
            if (!a || !Array.isArray(a.achievements)) throw new Error('resposta inesperada da lista de conquistas');
            setRules(r.rules); setAchs(a.achievements);
        } catch (e) { notify(errMsg(e, 'Erro ao carregar'), errKind(e)); } finally { setLoading(false); }
    }, [subdomain, call, notify]);
    useEffect(() => { carregar(); }, [carregar]);

    const ligar = async (r) => {
        try {
            if (r.active) { await call('/rules/toggle-active', { id: r.id, active: false }); notify('Regra desligada', 'success'); return carregar(); }
            const pv = await call('/rules/preview', { id: r.id, limit: 5 });
            const acoes = (r.achievements || []).length + (r.push_actions || []).length;
            if (!acoes) return notify('Esta regra não tem nenhuma ação (conquista ou push).', 'error');
            const nomes = (pv.rows || []).filter((x) => x.met).slice(0, 5).map((x) => x.fullname || x.subject_id).join(', ');
            if (!(await confirmar(`Ligar "${r.name}"?\n\nAgora ${pv.met} de ${pv.evaluated} ${pv.scope === 'store' ? 'lojas' : 'vendedores'} batem a regra` +
                `${nomes ? ` (ex.: ${nomes})` : ''}.\nUma regra ligada concede conquista e manda push de verdade a cada ciclo.`, { titulo: 'Ligar regra', rotuloConfirmar: 'Ligar' }))) return;
            await call('/rules/toggle-active', { id: r.id, active: true }); notify('Regra ligada', 'success'); carregar();
        } catch (e) { notify(errMsg(e, 'Erro'), errKind(e)); }
    };
    const excluirRegra = async (r) => {
        if (!(await confirmar(`Excluir a regra "${r.name}"? As concessões já feitas ficam.`, { titulo: 'Excluir regra', perigo: true, rotuloConfirmar: 'Excluir' }))) return;
        try { await call('/rules/delete', { id: r.id }); notify('Regra excluída', 'success'); carregar(); } catch (e) { notify(errMsg(e, 'Erro'), errKind(e)); }
    };
    const excluirAch = async (a) => {
        if (!(await confirmar(`Excluir a conquista "${a.title}"? O histórico de quem ganhou fica, mas ela some do app.`, { titulo: 'Excluir conquista', perigo: true, rotuloConfirmar: 'Excluir' }))) return;
        try { await call('/achievements/delete', { id: a.id }); notify('Conquista excluída', 'success'); carregar(); } catch (e) { notify(errMsg(e, 'Erro'), errKind(e)); }
    };
    const alternarAch = async (a) => {
        try { await call('/achievements/toggle-active', { id: a.id, active: !a.active }); carregar(); } catch (e) { notify(errMsg(e, 'Erro'), errKind(e)); }
    };
    const verConcessoes = async (a) => {
        try {
            const r = await call('/rules/grants', { achievement_id: a.id, scope: a.subject_scope, limit: 50 });
            setGrants({ ach: a, list: Array.isArray(r?.grants) ? r.grants : [] });
        } catch (e) { notify(errMsg(e, 'Erro'), errKind(e)); }
    };
    const abrirRegra = async (r) => {
        try { const g = await call('/rules/get', { id: r.id }); setFormRule(g.rule); } catch (e) { notify(errMsg(e, 'Erro'), errKind(e)); }
    };

    const achById = useMemo(() => Object.fromEntries(achs.map((a) => [a.id, a])), [achs]);

    return (
        <div className="rg-page">
            {toast && <div className={`rg-toast rg-toast-${toast.type}`}>{toast.msg}</div>}
            <h1 className="rg-title">🏆 Regras e Conquistas</h1>
            <p className="rg-subtitle">Uma regra mede algo (atendimentos, gravações, eventos, feedbacks, nota…) com os padrões do produto já aplicados e,
                quando bate, concede uma conquista e/ou manda um push. Ligar uma regra age sobre vendedores de verdade.</p>
            <div className="rg-row">
                <label>Empresa</label>
                <select className="rg-in" value={subdomain} onChange={(e) => setSubdomain(e.target.value)}>
                    <option value="">Selecione…</option>
                    {companies.map((c) => <option key={c.subdomain || c.id} value={c.subdomain}>{c.name || c.subdomain}</option>)}</select>
                {loading && <span className="rg-hint">carregando…</span>}
            </div>
            {subdomain && registry && (<>
                <div className="rg-tabs">
                    <button className={tab === 'rules' ? 'on' : ''} onClick={() => setTab('rules')}>Regras ({rules.length})</button>
                    <button className={tab === 'ach' ? 'on' : ''} onClick={() => setTab('ach')}>Conquistas ({achs.length})</button></div>

                {tab === 'rules' && (<>
                    <div className="rg-actions"><button className="rg-btn rg-primary" onClick={() => setFormRule({})}>+ Nova regra</button></div>
                    <table className="rg-table"><thead><tr><th>Regra</th><th>Sujeito</th><th>Fonte · janela</th><th>Quando</th><th>Ações</th><th>Última avaliação</th><th></th></tr></thead>
                        <tbody>{rules.map((r) => (<tr key={r.id} className={r.active ? '' : 'rg-off'}>
                            <td><b>{r.name}</b><div className="rg-hint">{r.key}</div></td>
                            <td>{SCOPE_LABEL[r.subject_scope]}</td>
                            <td>{registry.sources[r.spec.source]?.label || r.spec.source}<div className="rg-hint">{WINDOW_LABEL[r.spec.window?.kind]}{r.spec.window?.days ? ` ${r.spec.window.days}` : ''}</div></td>
                            <td>{TRIGGER_LABEL[r.trigger_spec?.kind]}{r.trigger_spec?.days_before ? ` (${r.trigger_spec.days_before}d antes)` : ''}<div className="rg-hint">{r.schedule?.every_minutes ? `a cada ${r.schedule.every_minutes} min` : 'horários fixos'}</div></td>
                            <td>{(r.achievements || []).map((a) => <div key={a.id}>{achById[a.id]?.emoji} {a.title}</div>)}{(r.push_actions || []).map((a) => <div key={a.action_id}>📲 {a.title}</div>)}</td>
                            <td>{r.last_run ? <>{brDateTime(r.last_run.slot_at)}<div className="rg-hint">{r.last_run.status} · {r.last_run.met}/{r.last_run.evaluated} · +{r.last_run.granted}</div></> : '—'}</td>
                            <td className="rg-cell-actions">
                                <button className={`rg-btn ${r.active ? '' : 'rg-primary'}`} onClick={() => ligar(r)}>{r.active ? 'Desligar' : 'Ligar'}</button>
                                <button className="rg-btn" onClick={() => abrirRegra(r)}>Editar</button>
                                <button className="rg-x" onClick={() => excluirRegra(r)}>🗑</button></td></tr>))}
                            {rules.length === 0 && <tr><td colSpan="7" className="rg-empty">Nenhuma regra ainda.</td></tr>}</tbody></table></>)}

                {tab === 'ach' && (<>
                    <div className="rg-actions"><button className="rg-btn rg-primary" onClick={() => setFormAch({})}>+ Nova conquista</button></div>
                    <table className="rg-table"><thead><tr><th>Conquista</th><th>Tipo</th><th>Sujeito</th><th>Frequência / níveis</th><th>Concedida</th><th>Regras</th><th></th></tr></thead>
                        <tbody>{achs.map((a) => (<tr key={a.id} className={a.active ? '' : 'rg-off'}>
                            <td><span className="rg-badge" style={{ background: a.color || '#999' }}>{a.emoji} {a.title}</span><div className="rg-hint">{a.key}</div></td>
                            <td>{KIND_LABEL[a.kind]}</td><td>{SCOPE_LABEL[a.subject_scope]}</td>
                            <td>{a.kind === 'leveled' ? (a.levels || []).map((l) => `${l.key} ≥ ${l.min}`).join(' · ') : FREQ_LABEL[a.frequency?.kind]}</td>
                            <td>{a.grants} <span className="rg-hint">({a.subjects} {a.subject_scope === 'store' ? 'lojas' : 'vendedores'})</span></td>
                            <td>{(a.rules || []).join(', ') || '—'}</td>
                            <td className="rg-cell-actions">
                                <button className="rg-btn" onClick={() => verConcessoes(a)}>Quem ganhou</button>
                                <button className="rg-btn" onClick={() => alternarAch(a)}>{a.active ? 'Esconder' : 'Mostrar'}</button>
                                <button className="rg-btn" onClick={() => setFormAch(a)}>Editar</button>
                                <button className="rg-x" onClick={() => excluirAch(a)}>🗑</button></td></tr>))}
                            {achs.length === 0 && <tr><td colSpan="7" className="rg-empty">Nenhuma conquista ainda.</td></tr>}</tbody></table></>)}
            </>)}

            {formRule && registry && <RuleForm key={formRule.id || 'new'} initial={formRule.id ? formRule : null} achievements={achs} registry={registry} call={call} notify={notify}
                onClose={() => { setFormRule(null); carregar(); }} onSaved={(r) => { setFormRule(r); carregar(); }} />}
            {formAch && <AchievementForm key={formAch.id || 'new'} initial={formAch.id ? formAch : null} call={call} notify={notify}
                onClose={() => setFormAch(null)} onSaved={() => { setFormAch(null); carregar(); }} />}
            {grants && (<div className="rg-modal"><div className="rg-card">
                <h2>{grants.ach.emoji} {grants.ach.title} — concessões recentes</h2>
                <table className="rg-table"><thead><tr><th>Quem</th><th>Nível</th><th>Quando</th><th>Valor</th></tr></thead>
                    <tbody>{grants.list.map((g) => (<tr key={g.id}><td>{g.fullname || g.subject_id}</td><td>{g.level_key || '—'}</td><td>{brDateTime(g.granted_at)}</td><td>{num(g.value)}</td></tr>))}
                        {grants.list.length === 0 && <tr><td colSpan="4" className="rg-empty">Ninguém ganhou ainda.</td></tr>}</tbody></table>
                <div className="rg-actions"><button className="rg-btn" onClick={() => setGrants(null)}>Fechar</button></div></div></div>)}
        </div>
    );
}

export default Regras;
